import React from 'react';
import { connect } from 'react-redux';
import { Card, Button, Icon, Tree, TextInput, LoadingOverlay, ConfirmModal, StatusModal } from '../../components/ui';
import { EmptyEditor } from './EntityEditor';
import SetupNavHead from './SetupNavHead';
import PlaceEditor from './editors/PlaceEditor';
import UserEditor from './editors/UserEditor';
import GateGuardsPanel from './editors/GateGuardsPanel';
import UsersTab from './tabs/UsersTab';
import { filterTree, expandableIds } from './shared';
import { roleLabel } from '../../roleLabels';
import { setAdminState, setAdminControl, getSubscribers, getCustomers, getSites, getGates, getUsers, getAssignments } from '../../slices';

/*
 * UnifiedSetupPane — the single "places tree + context tabs" Setup workspace that replaces the
 * three separate Organization / Users & Guards / Gate Assignments screens.
 *
 *  LEFT  = ONE org tree, PLACES ONLY: Subscriber ▸ Customer ▸ Site ▸ Gate (role-scoped; the
 *          backend-scoped lists decide what each role sees). Each gate carries a coverage badge
 *          (guard name / "N guards" / amber "Unassigned"). Search + filter chips span the tree.
 *          Roots by role: Super = every subscriber, plus "Dividia · Platform" for users with no
 *          subscriber; Account Admin = its own subscriber; Site Admin = its customers (or sites
 *          for a direct account).
 *  RIGHT = context-tabbed detail for the selected node:
 *          Subscriber → Overview · Customers|Sites · Users   (no Overview for an Account Admin —
 *                       the subscriber's name/license/account type are Dividia's to edit)
 *          Platform   → Users
 *          Customer   → Overview · Sites · Users        (Users = the whole "Users & Guards" section)
 *          Site       → Overview · Gates · Staff
 *          Gate       → Overview · Guards               (assign / reassign / +New guard, in place)
 *
 * Every editor is the SAME CRUD extracted from the old panes (endpoints/bodies verbatim), wrapped
 * in the tab shell — no reimplementation. Role scoping is preserved (derived from the scoped lists
 * + the same authId gates the old panes used); people live only in tabs, never loose in the tree.
 */
const KIND_LABEL = { subscriber: 'Subscriber', customer: 'Customer', site: 'Site', gate: 'Gate', platform: 'Platform' };
const KIND_ICON = { subscriber: 'brand', customer: 'company', site: 'location', gate: 'gate', platform: 'brand' };
const PLATFORM_NAME = 'Dividia · Platform';

export class UnifiedSetupPane extends React.Component {
  state = {
    sel: null,          // { kind, id } — selected existing node
    newChild: null,     // { kind, parentKind, parentId, newCtx } — a create form (overrides detail)
    tab: 'overview',
    search: '',
    gateFilter: '',     // '' | 'unassigned' | 'active'
    // an Account Admin's tree starts at their own subscriber — open it so the customers show at once
    expanded: new Set(this.props && this.props.authId === 1 && this.props.subscriberId ? [`subscriber:${this.props.subscriberId}`] : []),
    activeDirty: false,  // the active editor reports its dirtiness here (for the nav guard)
    pendingNav: null,    // action held behind the discard-unsaved confirm
    saveResult: null,    // user save/delete result modal — lifted here so it survives the editor unmounting
    pendingUserOpen: null, // a person picked from search → open them in their place's Users tab
  };

  // people matching the org search — searched separately from the PLACES tree so searching a person
  // (any role, not just guards) surfaces the PERSON and jumps to their editable record.
  peopleMatches = (query) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return [];
    return (this.props.users || [])
      .filter((u) => u.GateCustomer || u.GateSubscriber || this.isSuper())   // [86bcfugmv] Super: no-subscriber users open under Dividia · Platform
      .filter((u) => !u.deletedAt && (`${u.sFullName || ''} ${u.sUsername || ''}`).toLowerCase().includes(needle))
      .slice()
      .sort((a, b) => (a.sFullName || '').localeCompare(b.sFullName || ''))
      .slice(0, 30);
  };
  // navigate to a person's editable record: select their place (customer, else subscriber) and open
  // them in its Users tab. Clears the search so the tree shows the place in context.
  selectPerson = (u) => this.guardNav(() => {
    // [86bcfugmv] open the customer only if it's in the tree (the user list carries deleted customers);
    // otherwise the subscriber (Super / Account Admin), or Dividia · Platform for no subscriber at all
    const custLive = u.GateCustomer && (this.props.customers || []).some((c) => String(c.id) === String(u.GateCustomer.id));
    const scopeNode = custLive ? { kind: 'customer', id: u.GateCustomer.id }
      : (u.GateSubscriber ? ((this.isSuper() || this.ownSubscriber()) ? { kind: 'subscriber', id: u.GateSubscriber.id } : null)
        : (this.isSuper() ? { kind: 'platform', id: 0 } : null));
    if (!scopeNode) return;
    this.setState((st) => {
      const expanded = new Set(st.expanded);
      if (u.GateSubscriber) expanded.add(`subscriber:${u.GateSubscriber.id}`);
      return { sel: scopeNode, tab: 'users', newChild: null, search: '', expanded, pendingUserOpen: u.id };
    });
  });

  onResult = (r) => this.setState({ saveResult: r });

  componentDidMount() { this.refreshAll(); }

  componentDidUpdate(prevProps) {
    // resolve a just-created node once the refreshed list arrives, then select it. The backend
    // returns id:0 on create (MariaDB upsert) AND names aren't unique across parents (two sites can
    // each have a "Main" gate), so resolve the NEW row as the one present now but absent from prev
    // (by id), disambiguated by name + PARENT — not by name alone.
    const ps = this._pendingSelect;
    if (ps) {
      const list = this.listFor(ps.kind);
      const prev = this.listFor(ps.kind, prevProps);
      if (list !== prev) {
        const fresh = list.filter((x) => !prev.some((p) => p.id === x.id));
        const parentOk = (x) => {
          if (!ps.parent || ps.parent.id == null) return true;
          const flat = x[ps.parent.field];
          const nested = x[ps.parent.nestedField] && x[ps.parent.nestedField].id;
          return String(flat != null ? flat : nested) === String(ps.parent.id);
        };
        const candidates = fresh.filter((x) => (x.sName || '') === ps.name && parentOk(x));
        const ent = ps.id ? list.find((x) => String(x.id) === String(ps.id))
          : (candidates.length === 1 ? candidates[0] : null);
        this._pendingSelect = null;
        if (ent) this.setState({ sel: { kind: ps.kind, id: ent.id }, newChild: null, tab: 'overview' });
      }
    }
  }

  // ---- data ----
  listFor = (kind, props = this.props) => ({ subscriber: props.subscribers, customer: props.customers, site: props.sites, gate: props.gates }[kind] || []);
  refreshAll = async () => {
    await this.props.setAdminState({ field: 'adminListLoading', value: true });
    await this.props.getSubscribers();
    await this.props.getCustomers();
    await this.props.getSites();
    await this.props.getGates();
    await this.props.getUsers();
    await this.props.getAssignments();
    await this.props.setAdminState({ field: 'adminListLoading', value: false });
  };
  refreshUsers = async () => { const a = await this.props.getUsers(); return (a && a.payload && a.payload.users) || this.props.users || []; };
  refreshAssignments = async () => { await this.props.getGates(); await this.props.getUsers(); await this.props.getAssignments(); };
  refreshLists = async () => { await this.props.getSubscribers(); await this.props.getCustomers(); await this.props.getSites(); await this.props.getGates(); await this.props.getUsers(); await this.props.getAssignments(); };

  // ---- role scoping (mirrors the old panes: super → subscriber roots; else customers, or sites for direct) ----
  isSuper = () => this.props.authId === 100;
  isDirect = () => !this.isSuper() && (this.props.accountType || 'agency') === 'direct';
  // [86bbxga1j] Only Super + Account Admin (the subscriber-level admin) create customers/sites; Site
  // Admins (2) manage gates + users WITHIN their granted sites but can't mint new sites/customers.
  canCreateTenancy = () => this.props.authId === 100 || this.props.authId === 1;
  // [86bcfugmv] An Account Admin manages a whole subscriber, so its tree starts at that subscriber —
  // exactly like the Super's view of it. Its Users tab is the only place account-level users (peer
  // Account Admins, who carry no customer) can show; a customers-only tree had nowhere to put them,
  // so an Account Admin the customer created vanished from their view. null for every other role, or
  // until the subscriber list loads (the tree then falls back to customer/site roots).
  ownSubscriber = () => (this.props.authId === 1
    ? (this.props.subscribers || []).find((s) => String(s.id) === String(this.props.subscriberId)) || null
    : null);
  isDirectSub = (sub) => ((sub && sub.accountType) || (this.isSuper() ? null : this.props.accountType) || 'agency') === 'direct';
  // users with no subscriber: Dividia's platform super users, plus any account that lost its subscriber
  platformUsers = () => (this.props.users || []).filter((u) => !u.deletedAt && !u.GateSubscriber);

  // ---- assignment/coverage helpers (for gate badges) ----
  assignmentsByGate = () => {
    const map = {};
    (this.props.assignments || []).forEach((a) => { const gid = a.Gate ? a.Gate.id : a.GateId; if (gid == null) return; (map[gid] = map[gid] || []).push(a); });
    return map;
  };
  gateBadge = (gate, byGate) => {
    const as = byGate[gate.id] || [];
    if (as.length === 0) return <span className="minipill minipill--warn">Unassigned</span>;
    if (as.length === 1) return <span className="minipill on">{as[0].GateUser ? as[0].GateUser.sFullName : 'Guard'}</span>;
    return <span className="minipill on">{as.length} guards</span>;
  };

  addBtn = (title, onClick) => (
    <button type="button" className="tree-addbtn" title={title} aria-label={title} onClick={onClick}>
      <Icon name="plus" size={13} />
    </button>
  );

  // ---- tree model (places only, role-scoped, with gate coverage badges + contextual +add) ----
  buildTree = () => {
    const { customers, sites, gates, subscribers } = this.props;
    const byGate = this.assignmentsByGate();
    const gf = this.state.gateFilter;
    const gateOk = (g) => gf === '' || (gf === 'unassigned' ? (byGate[g.id] || []).length === 0 : !!g.fActive);

    const gatesBySite = {};
    (gates || []).forEach((g) => { if (!gateOk(g)) return; const sid = g.GateSite ? g.GateSite.id : g.GateSiteId; if (sid == null) return; (gatesBySite[sid] = gatesBySite[sid] || []).push(g); });

    const gateNode = (g) => ({
      id: `gate:${g.id}`,
      // PLACES search only (gate/site/customer). People are searched separately (see peopleMatches)
      // so a person's name surfaces the PERSON — not the gate they happen to guard.
      name: `${g.sName} ${g.GateSite ? g.GateSite.sName : ''} ${g.GateCustomer ? g.GateCustomer.sName : ''}`,
      label: (
        <span className="row" style={{ gap: 8, alignItems: 'center', width: '100%' }}>
          <Icon name="gate" size={16} className={`gicon ${g.fActive ? 'gicon--on' : 'gicon--off'}`} title={g.fActive ? 'Active gate' : 'Inactive gate'} />
          <span className="truncate grow">{g.sName}</span>
          {this.gateBadge(g, byGate)}
        </span>
      ),
    });
    const siteNode = (s) => {
      const kids = (gatesBySite[s.id] || []).slice().sort((a, b) => (a.sName || '').localeCompare(b.sName || ''));
      return {
        id: `site:${s.id}`, name: s.sName || s.name, label: s.sName || s.name, icon: <Icon name="location" size={15} />,
        count: kids.length,
        action: this.addBtn('Add gate to this site', (e) => { e.stopPropagation(); this.startNewChild('gate', 'site', s.id); }),
        children: kids.map(gateNode),
      };
    };
    const sitesByCust = {};
    (sites || []).forEach((s) => { const cid = s.GateCustomerId != null ? s.GateCustomerId : (s.GateCustomer ? s.GateCustomer.id : null); if (cid != null) (sitesByCust[cid] = sitesByCust[cid] || []).push(s); });
    const customerNode = (c) => {
      const kids = (sitesByCust[c.id] || []).slice().sort((a, b) => (a.sName || '').localeCompare(b.sName || '')).map(siteNode);
      return {
        id: `customer:${c.id}`, name: c.sName, label: c.sName, icon: <Icon name="company" size={15} />,
        count: kids.length,
        action: this.canCreateTenancy() ? this.addBtn('Add site to this customer', (e) => { e.stopPropagation(); this.startNewChild('site', 'customer', c.id); }) : undefined,
        children: kids,
      };
    };

    const custsBySub = {};
    (customers || []).forEach((c) => { const sid = c.GateSubscriberId != null ? c.GateSubscriberId : (c.GateSubscriber ? c.GateSubscriber.id : null); if (sid != null) (custsBySub[sid] = custsBySub[sid] || []).push(c); });
    const subscriberNode = (sub) => {
      const direct = this.isDirectSub(sub);
      const directSites = (custsBySub[sub.id] || []).reduce((acc, c) => acc.concat(sitesByCust[c.id] || []), []);
      const kids = direct ? directSites.slice().sort((a, b) => (a.sName || '').localeCompare(b.sName || '')).map(siteNode) : (custsBySub[sub.id] || []).slice().sort((a, b) => (a.sName || '').localeCompare(b.sName || '')).map(customerNode);
      return {
        id: `subscriber:${sub.id}`, name: sub.sName, label: sub.sName, icon: <Icon name="brand" size={15} />,
        count: kids.length,
        action: direct
          ? this.addBtn('Add site to this subscriber', (e) => { e.stopPropagation(); this.startNewChild('site', 'subscriber', sub.id); })
          : this.addBtn('Add customer to this subscriber', (e) => { e.stopPropagation(); this.startNewChild('customer', 'subscriber', sub.id); }),
        children: kids,
      };
    };

    let roots;
    const own = this.ownSubscriber();
    if (this.isSuper()) {
      roots = (subscribers || []).slice().sort((a, b) => (a.sName || '').localeCompare(b.sName || '')).map(subscriberNode);
      // [86bcfugmv] users with no subscriber get a place again (the old Users & Guards screen had this
      // "Dividia · Platform" root) — so an account that lost its subscriber can be found and repaired.
      const platform = this.platformUsers();
      if (platform.length) {
        const unattached = platform.filter((u) => !(u.GateAcl && u.GateAcl.id === 100)).length;
        roots.unshift({
          id: 'platform:0', name: 'Dividia Platform', icon: <Icon name="brand" size={15} />, count: platform.length,
          label: (
            <span className="row" style={{ gap: 8, alignItems: 'center', width: '100%' }}>
              <span className="truncate grow">{PLATFORM_NAME}</span>
              {unattached > 0 && <span className="minipill minipill--warn">{unattached} without a subscriber</span>}
            </span>
          ),
        });
      }
    } else if (own) {
      roots = [subscriberNode(own)];
    } else if (this.isDirect()) {
      roots = (sites || []).slice().sort((a, b) => (a.sName || '').localeCompare(b.sName || '')).map(siteNode);
    } else {
      roots = (customers || []).slice().sort((a, b) => (a.sName || '').localeCompare(b.sName || '')).map(customerNode);
    }

    // prune empty ancestors only when a gate filter is active (so a filter reads flat, but the
    // un-filtered tree still shows empty customers/sites you can add children to).
    if (gf) roots = this.pruneEmpty(roots);
    return roots;
  };
  pruneEmpty = (nodes) => (nodes || []).map((n) => {
    if (!n.children) return n;
    const kids = this.pruneEmpty(n.children);
    return { ...n, children: kids };
  }).filter((n) => {
    if (String(n.id).indexOf('gate:') === 0) return true;      // gate leaves always kept
    return n.children && n.children.length > 0;                  // places kept only if they still hold gates
  });

  allNodeIds = (nodes) => { const ids = []; const walk = (n) => { ids.push(n.id); (n.children || []).forEach(walk); }; (nodes || []).forEach(walk); return ids; };

  // ---- selection + navigation guard ----
  onDirtyChange = (d) => { if (d !== this.state.activeDirty) this.setState({ activeDirty: d }); };
  guardNav = (action) => { if (this.state.activeDirty) this.setState({ pendingNav: action }); else action(); };

  onTreeToggle = (id) => this.setState((st) => { const next = new Set(st.expanded); next.has(id) ? next.delete(id) : next.add(id); return { expanded: next }; });
  onTreeSelect = (id) => {
    const [kind, rawId] = id.split(':');
    this.selectNode(kind, parseInt(rawId, 10));
  };
  selectNode = (kind, id) => this.guardNav(() => this.setState((st) => ({ sel: { kind, id }, newChild: null, tab: st.sel && st.sel.kind === kind && st.sel.id === id ? st.tab : 'overview' })));
  setTab = (tab) => this.guardNav(() => this.setState({ tab }));

  // ---- create flows ----
  startNewChild = (kind, parentKind, parentId, extra = {}) => this.guardNav(() => {
    const newCtx = this.newCtxFor(kind, parentKind, parentId, extra);
    // expand the parent so the new record's context is visible
    this.setState((st) => {
      const expanded = new Set(st.expanded);
      if (parentKind && parentId) expanded.add(`${parentKind}:${parentId}`);
      return { newChild: { kind, parentKind, parentId, newCtx }, expanded, tab: 'overview' };
    });
  });
  newCtxFor = (kind, parentKind, parentId, extra) => {
    const find = (list, id) => (list || []).find((x) => String(x.id) === String(id));
    if (kind === 'customer') { const sub = find(this.props.subscribers, parentId); return { subscriberId: sub ? sub.id : parentId }; }
    if (kind === 'site') {
      if (parentKind === 'subscriber') { // direct subscriber → its implicit customer
        const cust = (this.props.customers || []).find((c) => String(c.GateSubscriberId) === String(parentId));
        return { subscriberId: parentId, customerId: cust ? cust.id : 0, customerName: cust ? cust.sName : '', direct: true };
      }
      const c = find(this.props.customers, parentId);
      return { customerId: parentId, subscriberId: c ? c.GateSubscriberId : 0, customerName: c ? c.sName : '' };
    }
    if (kind === 'gate') {
      const s = find(this.props.sites, parentId);
      return { siteId: parentId, siteName: s ? s.sName : '', customerId: s ? s.GateCustomerId : 0, customerName: s ? s.sCustomerName : '', subscriberId: s ? s.GateSubscriberId : 0 };
    }
    if (kind === 'user') return { customerId: extra.customerId || 0, subscriberId: extra.subscriberId || 0, typeId: extra.typeId, gateId: extra.gateId };
    return {};
  };

  // parent FK ref for resolving a just-created node unambiguously (see componentDidUpdate)
  parentRefFor = (kind, ctx = {}) => {
    if (kind === 'gate') return { field: 'GateSiteId', nestedField: 'GateSite', id: ctx.siteId };
    if (kind === 'site') return { field: 'GateCustomerId', nestedField: 'GateCustomer', id: ctx.customerId };
    if (kind === 'customer') return { field: 'GateSubscriberId', nestedField: 'GateSubscriber', id: ctx.subscriberId };
    return null;
  };
  onChildSaved = ({ kind, id, name }) => {
    const nc = this.state.newChild;
    this._pendingSelect = { kind, id, name, parent: nc ? this.parentRefFor(kind, nc.newCtx) : null };
    this.setState({ newChild: null });
    this.refreshLists();
  };
  onChildDeleted = ({ kind, row }) => {
    // select the parent of the deleted node (so the tree stays oriented), else clear
    const parent = this.parentOf(kind, row);
    this.setState({ sel: parent, newChild: null, tab: 'overview', activeDirty: false });
    this.refreshLists();
  };
  parentOf = (kind, row) => {
    if (kind === 'gate') return row.GateSite ? { kind: 'site', id: row.GateSite.id } : null;
    if (kind === 'site') return row.GateCustomerId ? { kind: 'customer', id: row.GateCustomerId } : null;
    if (kind === 'customer') return (this.isSuper() || this.ownSubscriber()) && row.GateSubscriberId ? { kind: 'subscriber', id: row.GateSubscriberId } : null;
    return null;
  };
  // a place editor saved (rename/toggle) — refresh lists so badges/counts/labels update, keep selection
  onPlaceEdited = () => this.refreshLists();
  // a user saved/deleted inside a Users tab — refresh so tree badges (guard coverage) update
  onUsersChanged = () => this.refreshAll();
  // a guard created via a gate's "New guard" — return to that gate's Guards tab, refresh coverage
  onGuardCreated = () => { this.setState({ newChild: null, tab: 'guards', activeDirty: false }); this.refreshAll(); };

  // ---- lookups for the detail ----
  selectedNode = () => {
    const { sel } = this.state;
    if (!sel) return null;
    if (sel.kind === 'platform') return { id: 0, sName: PLATFORM_NAME };   // a place for users with no subscriber
    return this.listFor(sel.kind).find((x) => x.id === sel.id) || null;
  };
  subName = (id) => { const s = (this.props.subscribers || []).find((x) => x.id === id); return s ? s.sName : ''; };

  tabsFor = (kind, node) => {
    if (kind === 'platform') return ['users'];
    if (kind === 'subscriber') {
      const kids = this.isDirectSub(node) ? 'sites' : 'customers';
      // the subscriber's Overview (name, license, account type) is Dividia's to edit — Super only
      return this.isSuper() ? ['overview', kids, 'users'] : [kids, 'users'];
    }
    if (kind === 'customer') return ['overview', 'sites', 'users'];
    if (kind === 'site') return ['overview', 'gates', 'staff'];
    return ['overview', 'guards'];
  };
  tabLabel = (tab) => ({ overview: 'Overview', customers: 'Customers', sites: 'Sites', gates: 'Gates', users: 'Users', staff: 'Staff', guards: 'Guards' }[tab] || tab);

  crumbsFor = (kind, node) => {
    if (kind === 'subscriber') return [];
    if (kind === 'customer') return this.isSuper() ? [this.subName(node.GateSubscriberId)] : [];
    if (kind === 'site') return (this.isSuper() ? [node.sSubscriberName, node.sCustomerName] : [node.sCustomerName]).filter(Boolean);
    if (kind === 'gate') return [node.GateCustomer && node.GateCustomer.sName, node.GateSite && node.GateSite.sName].filter(Boolean);
    return [];
  };

  render() {
    const { adminListLoading } = this.props;
    const fullTree = this.buildTree();
    const searchActive = this.state.search.trim().length > 0;
    const filterActive = this.state.gateFilter !== '';
    const filtered = searchActive ? filterTree(fullTree, this.state.search) : null;
    const nodes = searchActive ? filtered.nodes : fullTree;
    const people = searchActive ? this.peopleMatches(this.state.search) : [];
    const treeExpanded = searchActive ? filtered.expand : (filterActive ? expandableIds(nodes) : this.state.expanded);
    const selectedId = this.state.sel ? `${this.state.sel.kind}:${this.state.sel.id}` : (this.state.newChild && this.state.newChild.parentId ? `${this.state.newChild.parentKind}:${this.state.newChild.parentId}` : undefined);
    const rootAdd = this.isSuper()
      ? { label: 'Subscriber', fn: () => this.startNewChild('subscriber', null, null) }
      : !this.canCreateTenancy()
        ? null                                    // [86bbxga1j] site-admins can't create customers/sites
        : this.isDirect()
          ? { label: 'Site', fn: () => this.startNewChild('site', 'subscriber', this.props.subscriberId) }
          : { label: 'Customer', fn: () => this.startNewChild('customer', 'subscriber', this.props.subscriberId) };

    return (
      <div className="setup">
        {/* LEFT — the one places tree */}
        <div className="setup__nav">
          <Card flush style={{ position: 'relative' }}>
            <LoadingOverlay show={adminListLoading} label="Loading" />
            <SetupNavHead title="Organization" onBack={() => this.guardNav(() => this.props.setAdminControl(false))}>
              {rootAdd && <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={rootAdd.fn}>{rootAdd.label}</Button>}
            </SetupNavHead>
            <div className="setup__search">
              <TextInput search value={this.state.search} onChange={(e) => this.setState({ search: e.target.value })} placeholder="Search organization…" aria-label="Search organization" />
            </div>
            <div className="setup__chips">
              {[{ k: '', l: 'All' }, { k: 'unassigned', l: 'Unassigned gates' }, { k: 'active', l: 'Active' }].map((c) => (
                <button key={c.k} type="button" className={`chip${this.state.gateFilter === c.k ? ' on' : ''}`} onClick={() => this.setState({ gateFilter: c.k })}>{c.l}</button>
              ))}
            </div>
            <div className="setup__scroll setup__scroll--tree">
              {searchActive && people.length > 0 && (
                <div className="setup__people">
                  <div className="setup__resulthdr">People ({people.length})</div>
                  {people.map((u) => {
                    const acl = u.GateAcl && u.GateAcl.id;
                    const at = u.GateCustomer ? u.GateCustomer.sName : (u.GateSubscriber ? u.GateSubscriber.sName : '');
                    return (
                      <button type="button" key={u.id} className="peoplerow" onClick={() => this.selectPerson(u)} title={`Open ${u.sFullName}`}>
                        <Icon name="user" size={15} className="peoplerow__ic" />
                        <span className="peoplerow__body grow">
                          <span className="peoplerow__line1">
                            <span className="peoplerow__name truncate">{u.sFullName}</span>
                            {acl != null && <span className={`minipill${acl === 3 ? ' on' : ''}`}>{roleLabel(acl)}</span>}
                          </span>
                          {at && <span className="peoplerow__at truncate">{at}</span>}
                        </span>
                      </button>
                    );
                  })}
                  {nodes.length > 0 && <div className="setup__resulthdr setup__resulthdr--places">Places ({nodes.length})</div>}
                </div>
              )}
              {nodes.length > 0 && <Tree nodes={nodes} expanded={treeExpanded} onToggle={this.onTreeToggle} selectedId={selectedId} onSelect={this.onTreeSelect} />}
              {nodes.length === 0 && !(searchActive && people.length > 0) && (
                <div className="sub" style={{ padding: 16, textAlign: 'center' }}>{searchActive ? <>No matches for &ldquo;{this.state.search.trim()}&rdquo;</> : (filterActive ? 'No gates match this filter.' : 'No records yet. Use the add buttons to create one.')}</div>
              )}
            </div>
            <div className="setup__navfoot">
              <span className="sub" style={{ fontSize: 12 }}>Use the <Icon name="plus" size={11} style={{ verticalAlign: '-1px' }} /> on any row to add a child</span>
              <div className="grow" />
              <Button size="sm" variant="ghost" onClick={() => this.guardNav(() => this.props.setAdminControl(false))}>Done</Button>
            </div>
          </Card>
        </div>

        {/* RIGHT — tabbed detail (or a create form) */}
        {this.renderDetail()}

        {this.state.pendingNav && (
          <ConfirmModal open onClose={() => this.setState({ pendingNav: null })} onCancel={() => this.setState({ pendingNav: null })}
            onConfirm={() => { const act = this.state.pendingNav; this.setState({ pendingNav: null, activeDirty: false }, act); }}
            title="Discard unsaved changes?" confirmLabel="Discard changes" cancelLabel="Keep editing" danger>
            <p className="ui-modal__msg">You've made changes that haven't been saved.</p>
          </ConfirmModal>
        )}
        {this.state.saveResult && (
          <StatusModal open status={this.state.saveResult.status} title={this.state.saveResult.title} message={this.state.saveResult.message} onClose={() => this.setState({ saveResult: null })} />
        )}
      </div>
    );
  }

  renderDetail() {
    const nc = this.state.newChild;
    if (nc) {
      // a focused create form (place or user), parent shown as context
      const crumb = nc.newCtx && (nc.newCtx.customerName || (nc.parentKind === 'subscriber' ? this.subName(nc.parentId) : ''));
      if (nc.kind === 'user') {
        const isGuard = String(nc.newCtx.typeId) === '3';
        return (
          <div className="detail">
            <div className="detail__head">
              <div className="crumbs">{crumb ? <b>{crumb}</b> : null}{crumb ? <span className="sepc">›</span> : null}<b>{isGuard ? 'New guard' : 'New user'}</b></div>
              <div className="spread"><h2 className="h-page">{isGuard ? 'New guard' : 'New user'}</h2></div>
            </div>
            <div className="detail__body">
              <UserEditor
                key="newuser"
                row={null}
                newCtx={nc.newCtx}
                authId={this.props.authId}
                userId={this.props.userId}
                subscriberId={this.props.subscriberId}
                webToken={this.props.webToken}
                customers={this.props.customers}
                subscribers={this.props.subscribers}
                sites={this.props.sites}
                gates={this.props.gates}
                assignments={this.props.assignments}
                toast={this.props.toast}
                refreshUsers={this.refreshUsers}
                refreshAssignments={this.refreshAssignments}
                onSaved={this.onGuardCreated}
                onCancelNew={() => this.setState({ newChild: null, tab: 'guards', activeDirty: false })}
                onDirtyChange={this.onDirtyChange}
                onResult={this.onResult}
              />
            </div>
          </div>
        );
      }
      return (
        <div className="detail">
          <div className="detail__head">
            <div className="crumbs">{crumb ? <b>{crumb}</b> : null}{crumb ? <span className="sepc">›</span> : null}<b>New {KIND_LABEL[nc.kind]}</b></div>
            <div className="spread"><h2 className="h-page">New {KIND_LABEL[nc.kind]}</h2><span className={`kindpill kindpill--${nc.kind}`}>{KIND_LABEL[nc.kind]}</span></div>
          </div>
          <div className="detail__body">
            <PlaceEditor
              key={`new-${nc.kind}-${nc.parentId}`}
              kind={nc.kind}
              row={null}
              newCtx={nc.newCtx}
              authId={this.props.authId}
              webToken={this.props.webToken}
              sites={this.props.sites}
              toast={this.props.toast}
              onSaved={this.onChildSaved}
              onCancelNew={() => this.setState({ newChild: null, activeDirty: false })}
              onDirtyChange={this.onDirtyChange}
            />
          </div>
        </div>
      );
    }

    const node = this.selectedNode();
    if (!node) {
      return <EmptyEditor icon="tree" title="Select a place to manage"
        message="Pick a subscriber, customer, site, or gate from the tree — or use the + on any row to add one. Each place shows its details, its children, and its people in tabs." />;
    }
    const { sel, tab } = this.state;
    const tabs = this.tabsFor(sel.kind, node);
    const activeTab = tabs.includes(tab) ? tab : tabs[0];
    const crumbs = this.crumbsFor(sel.kind, node);
    const title = node.sName || node.name || KIND_LABEL[sel.kind];

    return (
      <div className="editor detail">
        <div className="detail__head">
          {crumbs.filter(Boolean).length > 0 && (
            <div className="crumbs">{crumbs.filter(Boolean).map((c, i) => <React.Fragment key={i}>{i > 0 && <span className="sepc">›</span>}<b>{c}</b></React.Fragment>)}</div>
          )}
          <div className="spread">
            <h2 className="h-page truncate">{title}</h2>
            <div className="pillrow">
              {sel.kind === 'gate' && <span className={`pill pill--${node.fActive ? 'in' : 'auth'}`}>{node.fActive ? 'Active' : 'Inactive'}</span>}
              <span className={`kindpill kindpill--${sel.kind}`}>{KIND_LABEL[sel.kind]}</span>
            </div>
          </div>
        </div>
        <div className="detail__tabs" role="tablist">
          {tabs.map((t) => (
            <button key={t} type="button" role="tab" aria-selected={activeTab === t} className={`detail__tab${activeTab === t ? ' on' : ''}`} onClick={() => this.setTab(t)}>
              {this.tabLabel(t)}{this.tabCount(sel.kind, node, t) != null && <span className="detail__tabn num">{this.tabCount(sel.kind, node, t)}</span>}
            </button>
          ))}
        </div>
        <div className="detail__body">{this.renderTabBody(sel.kind, node, activeTab)}</div>
      </div>
    );
  }

  tabCount = (kind, node, tab) => {
    const byGate = this.assignmentsByGate();
    if (tab === 'customers') return (this.props.customers || []).filter((c) => String(c.GateSubscriberId) === String(node.id)).length;
    if (tab === 'sites') {
      if (kind === 'subscriber') return (this.props.sites || []).filter((s) => String(s.GateSubscriberId) === String(node.id)).length;
      return (this.props.sites || []).filter((s) => String(s.GateCustomerId) === String(node.id)).length;
    }
    if (tab === 'gates') return (this.props.gates || []).filter((g) => (g.GateSite ? g.GateSite.id : g.GateSiteId) === node.id).length;
    if (tab === 'users') {
      if (kind === 'platform') return this.platformUsers().length;
      return (this.props.users || []).filter((u) => !u.deletedAt && (kind === 'customer' ? (u.GateCustomer && u.GateCustomer.id === node.id) : (u.GateSubscriber && u.GateSubscriber.id === node.id))).length;
    }
    if (tab === 'staff') { const gids = (this.props.gates || []).filter((g) => (g.GateSite ? g.GateSite.id : g.GateSiteId) === node.id).map((g) => g.id); return gids.reduce((n, gid) => n + (byGate[gid] || []).length, 0); }
    if (tab === 'guards') return (byGate[node.id] || []).length;
    return null;
  };

  usersTabProps = () => ({
    authId: this.props.authId, userId: this.props.userId, selfSubscriberId: this.props.subscriberId,
    webToken: this.props.webToken, users: this.props.users, customers: this.props.customers,
    subscribers: this.props.subscribers, sites: this.props.sites, gates: this.props.gates,
    assignments: this.props.assignments, toast: this.props.toast,
    refreshUsers: this.refreshUsers, refreshAssignments: this.refreshAssignments,
    onChanged: this.onUsersChanged, onDirtyChange: this.onDirtyChange, guardNav: this.guardNav, onResult: this.onResult,
    openUserId: this.state.pendingUserOpen, onOpenedUser: () => this.setState({ pendingUserOpen: null }),
  });

  renderTabBody(kind, node, tab) {
    if (tab === 'overview') {
      return (
        <PlaceEditor
          key={`${kind}:${node.id}`}
          kind={kind}
          row={node}
          authId={this.props.authId}
          webToken={this.props.webToken}
          sites={this.props.sites}
          toast={this.props.toast}
          onSaved={this.onPlaceEdited}
          onDeleted={this.onChildDeleted}
          onDirtyChange={this.onDirtyChange}
        />
      );
    }
    if (tab === 'guards') {
      return (
        <GateGuardsPanel
          gate={node}
          guards={this.props.guards}
          assignments={this.props.assignments}
          webToken={this.props.webToken}
          toast={this.props.toast}
          refreshAssignments={this.refreshAssignments}
          onNewGuard={(gate) => this.startNewChild('user', 'gate', gate.id, { customerId: gate.GateCustomer ? gate.GateCustomer.id : gate.GateCustomerId, subscriberId: gate.GateSubscriber ? gate.GateSubscriber.id : gate.GateSubscriberId, typeId: '3', gateId: gate.id })}
        />
      );
    }
    if (tab === 'users') {
      const scope = kind === 'customer' ? { customerId: node.id } : (kind === 'platform' ? { platform: true } : { subscriberId: node.id });
      return <UsersTab key={`users:${kind}:${node.id}`} scope={scope} {...this.usersTabProps()} />;
    }
    if (tab === 'customers') return this.renderChildList('customer', (this.props.customers || []).filter((c) => String(c.GateSubscriberId) === String(node.id)), node);
    if (tab === 'sites') {
      const list = kind === 'subscriber'
        ? (this.props.sites || []).filter((s) => String(s.GateSubscriberId) === String(node.id))
        : (this.props.sites || []).filter((s) => String(s.GateCustomerId) === String(node.id));
      return this.renderChildList('site', list, node);
    }
    if (tab === 'gates') return this.renderChildList('gate', (this.props.gates || []).filter((g) => (g.GateSite ? g.GateSite.id : g.GateSiteId) === node.id), node);
    if (tab === 'staff') return this.renderStaff(node);
    return null;
  }

  renderChildList(childKind, list, parent) {
    const byGate = this.assignmentsByGate();
    const addLabel = { customer: 'Add customer', site: 'Add site', gate: 'Add gate' }[childKind];
    const canAdd = childKind === 'gate' || this.canCreateTenancy();   // [86bbxga1j] site-admins add gates, not sites/customers
    const parentKind = this.state.sel.kind;
    const secondary = (row) => {
      if (childKind === 'site') { const n = (this.props.gates || []).filter((g) => (g.GateSite ? g.GateSite.id : g.GateSiteId) === row.id); const manned = n.filter((g) => (byGate[g.id] || []).length).length; return `${n.length} gate${n.length === 1 ? '' : 's'} · ${manned} manned`; }
      if (childKind === 'gate') { const as = byGate[row.id] || []; return as.length ? as.map((a) => a.GateUser && a.GateUser.sFullName).filter(Boolean).join(', ') : 'No guard assigned'; }
      if (childKind === 'customer') { const n = (this.props.sites || []).filter((s) => String(s.GateCustomerId) === String(row.id)).length; return `${n} site${n === 1 ? '' : 's'}`; }
      return '';
    };
    return (
      <div className="tabpane">
        <div className="toolbar">
          <div className="sectlabel">{list.length} {childKind === 'customer' ? (list.length === 1 ? 'customer' : 'customers') : childKind === 'site' ? (list.length === 1 ? 'site' : 'sites') : (list.length === 1 ? 'gate' : 'gates')}</div>
          {canAdd && <Button size="sm" icon={<Icon name="plus" size={14} />} onClick={() => this.startNewChild(childKind, parentKind, parent.id)}>{addLabel}</Button>}
        </div>
        {list.length === 0 ? (
          <div className="sub" style={{ padding: 16, textAlign: 'center' }}>Nothing here yet.{canAdd ? ` Use “${addLabel}” to create one.` : ''}</div>
        ) : (
          <div className="list">
            {list.slice().sort((a, b) => (a.sName || '').localeCompare(b.sName || '')).map((row) => (
              <div className="item" key={row.id}>
                <span className="ic"><Icon name={KIND_ICON[childKind]} size={16} /></span>
                <div className="grow"><div className="p">{row.sName}{childKind === 'gate' && !row.fActive ? <span className="dim"> · inactive</span> : ''}</div><div className="s">{secondary(row)}</div></div>
                {childKind === 'gate' && ((byGate[row.id] || []).length ? <span className="minipill on">Manned</span> : <span className="minipill minipill--warn">Unassigned</span>)}
                <Button size="sm" variant="default" onClick={() => this.selectNode(childKind, row.id)}>Open</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  renderStaff(site) {
    const byGate = this.assignmentsByGate();
    const gates = (this.props.gates || []).filter((g) => (g.GateSite ? g.GateSite.id : g.GateSiteId) === site.id);
    const rows = [];
    gates.forEach((g) => (byGate[g.id] || []).forEach((a) => rows.push({ name: a.GateUser ? a.GateUser.sFullName : 'Guard', gate: g })));
    return (
      <div className="tabpane">
        <div className="sectlabel">Guards working {site.sName} ({rows.length})</div>
        {rows.length === 0 ? (
          <div className="sub" style={{ padding: 16, textAlign: 'center' }}>No guards assigned to this site’s gates yet. Assign them from a gate’s Guards tab.</div>
        ) : (
          <div className="list">
            {rows.map((r, i) => (
              <div className="item" key={i}>
                <span className="ic"><Icon name="user" size={16} /></span>
                <div className="grow"><div className="p">{r.name}</div><div className="s">at {r.gate.sName}</div></div>
                <Button size="sm" variant="default" onClick={() => this.selectNode('gate', r.gate.id)}>Manage on gate</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  webToken: state.auth.webToken,
  authId: state.auth.authId,
  userId: state.auth.userId,
  accountType: state.auth.accountType,
  subscriberId: state.auth.subscriberId,
  customerId: state.auth.customerId,
  adminListLoading: state.admin.adminListLoading,
  subscribers: state.subscriber.subscribers,
  customers: state.customer.customers,
  sites: state.site.sites,
  gates: state.gate.gates,
  users: state.user.users,
  guards: state.user.guards,
  assignments: state.assignment.assignments,
});

export default connect(mapStateToProps, { setAdminState, setAdminControl, getSubscribers, getCustomers, getSites, getGates, getUsers, getAssignments })(UnifiedSetupPane);
